import { useState } from 'react';
import { Search, Pencil, RotateCcw, Layers } from 'lucide-react';
import { useProducts, useSaveOverride, useDeleteOverride } from '../../api/produits';
import { useCycles } from '../../api/referentiel';
import { useCapabilities } from '../../api/capabilities';
import type { ProductOverrideInput, ResolvedProduct, ResolvedValue, ValueSource } from '../../api/types';
import type { Cycle } from '../../api/types';
import { ApiErrorMessage, Modal } from '../referentiel/ResourcesPanel';

/** Petit badge indiquant d'ou vient une valeur resolue. */
function SourceBadge({ source }: { source: ValueSource }) {
  const map: Record<ValueSource, { label: string; cls: string }> = {
    override: { label: 'surchargé', cls: 'bg-blue-100 text-blue-700' },
    erp: { label: 'ERP', cls: 'bg-gray-100 text-gray-600' },
    default: { label: '—', cls: 'bg-gray-50 text-gray-400' },
  };
  const { label, cls } = map[source];
  return <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${cls}`}>{label}</span>;
}

function ResolvedCell({ resolved, render }: { resolved: ResolvedValue<unknown>; render: (v: unknown) => string }) {
  return (
    <span className="inline-flex items-center">
      <span className={resolved.source === 'default' ? 'text-gray-400' : 'text-gray-900'}>{render(resolved.value)}</span>
      {resolved.source !== 'default' && <SourceBadge source={resolved.source} />}
    </span>
  );
}

/** Panneau produits & surcharges (lot 3), intégré au référentiel. */
export default function ProductsPanel() {
  const [search, setSearch] = useState('');
  const { data: products, isLoading, error } = useProducts(search || undefined);
  const { data: cycles } = useCycles();
  const { tcFin } = useCapabilities();
  const [editing, setEditing] = useState<ResolvedProduct | null>(null);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Produits & surcharges</h2>
          <p className="text-xs text-gray-500">Les produits arrivent par import ERP ; on les surcharge ici (cycles alternatifs, Tc, voies).</p>
        </div>
        <div className="relative w-64">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            className="w-full border rounded pl-9 pr-3 py-2"
            placeholder="Rechercher une référence…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

        {isLoading && <p className="text-gray-500">Chargement des produits…</p>}
        {error && <p className="text-red-600">Impossible de charger les produits. Les produits arrivent par import ERP.</p>}

        {products && products.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Aucun produit. Ils apparaîtront après un import ERP (dataset « products »).
          </div>
        )}

        {products && products.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Référence</th>
                  <th className="px-4 py-3">Libellé</th>
                  <th className="px-4 py-3">Cycles autorisés</th>
                  {tcFin && <th className="px-4 py-3">Tc / pièce</th>}
                  <th className="px-4 py-3">Voies</th>
                  <th className="px-4 py-3">Partage</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.map((p) => (
                  <tr key={p.ref} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{p.ref}</td>
                    <td className="px-4 py-3 text-gray-600">{p.label}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center flex-wrap gap-1">
                        {p.allowedCycleCodes.value.length > 0 ? (
                          p.allowedCycleCodes.value.map((c) => (
                            <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-800">{c}</span>
                          ))
                        ) : (
                          <span className="text-gray-400">aucun</span>
                        )}
                        <SourceBadge source={p.allowedCycleCodes.source} />
                      </span>
                    </td>
                    {tcFin && (
                      <td className="px-4 py-3">
                        <ResolvedCell resolved={p.tcCount} render={(v) => (v == null ? '—' : String(v))} />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <ResolvedCell resolved={p.vacuumPorts} render={(v) => (v == null ? '—' : String(v))} />
                    </td>
                    <td className="px-4 py-3">
                      <ResolvedCell resolved={p.vacuumShareable} render={(v) => (v ? 'oui' : 'non')} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1" onClick={() => setEditing(p)}>
                        <Pencil size={16} /> Surcharger
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {editing && <OverrideModal product={editing} cycles={cycles ?? []} tcFin={tcFin} onClose={() => setEditing(null)} />}
    </div>
  );
}

function OverrideModal({ product, cycles, tcFin, onClose }: { product: ResolvedProduct; cycles: Cycle[]; tcFin: boolean; onClose: () => void }) {
  const save = useSaveOverride();
  const remove = useDeleteOverride();

  // Pre-remplissage : on part des valeurs surchargees existantes uniquement
  // (pas des valeurs ERP resolues), pour ne pas transformer un heritage ERP en
  // surcharge involontaire au premier enregistrement.
  const [form, setForm] = useState<ProductOverrideInput>({
    tcCount: product.tcCount.source === 'override' ? product.tcCount.value : null,
    vacuumPorts: product.vacuumPorts.source === 'override' ? product.vacuumPorts.value : null,
    vacuumShareable: product.vacuumShareable.source === 'override' ? product.vacuumShareable.value : false,
    notes: product.notes,
    allowedCycleIds: product.allowedCycleCodes.source === 'override'
      ? cycles.filter((c) => product.allowedCycleCodes.value.includes(c.code)).map((c) => c.id)
      : [],
  });

  const set = <K extends keyof ProductOverrideInput>(k: K, v: ProductOverrideInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCycle = (id: number) =>
    setForm((f) => ({
      ...f,
      allowedCycleIds: f.allowedCycleIds.includes(id) ? f.allowedCycleIds.filter((x) => x !== id) : [...f.allowedCycleIds, id],
    }));

  return (
    <Modal title={`Surcharge — ${product.ref}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ ref: product.ref, data: form }, { onSuccess: onClose });
        }}
        className="space-y-4"
      >
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Layers size={13} /> Laisser un champ vide = utiliser la valeur ERP. Renseigner = surcharger.
        </p>

        <div className="grid grid-cols-2 gap-4">
          {/* Tc par pièce : Option 2 uniquement. */}
          {tcFin && (
            <label className="block">
              <span className="text-sm text-gray-600">Tc par pièce</span>
              <input type="number" min={0} placeholder="valeur ERP" className="mt-1 w-full border rounded px-3 py-2"
                value={form.tcCount ?? ''} onChange={(e) => set('tcCount', e.target.value === '' ? null : Number(e.target.value))} />
            </label>
          )}
          <label className="block">
            <span className="text-sm text-gray-600">Voies de vide</span>
            <input type="number" min={0} placeholder="valeur ERP" className="mt-1 w-full border rounded px-3 py-2"
              value={form.vacuumPorts ?? ''} onChange={(e) => set('vacuumPorts', e.target.value === '' ? null : Number(e.target.value))} />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.vacuumShareable} onChange={(e) => set('vacuumShareable', e.target.checked)} />
          Voies de vide partageables entre pièces
        </label>

        <fieldset>
          <legend className="text-sm text-gray-600 mb-1">Cycles autorisés (alternatives)</legend>
          <p className="text-xs text-gray-400 mb-2">Vide = seul le cycle ERP du produit s'applique.</p>
          <div className="flex flex-wrap gap-2">
            {cycles.map((c) => {
              const on = form.allowedCycleIds.includes(c.id);
              return (
                <button type="button" key={c.id} onClick={() => toggleCycle(c.id)}
                  className={`px-3 py-1.5 rounded border text-sm ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                  {c.code}
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="block">
          <span className="text-sm text-gray-600">Notes</span>
          <textarea className="mt-1 w-full border rounded px-3 py-2" rows={2} value={form.notes ?? ''}
            onChange={(e) => set('notes', e.target.value || null)} />
        </label>

        <ApiErrorMessage error={save.error} />

        <div className="flex justify-between items-center pt-2">
          <button
            type="button"
            className="text-red-600 hover:text-red-800 text-sm inline-flex items-center gap-1 disabled:opacity-40"
            disabled={!product.hasOverride || remove.isPending}
            onClick={() => {
              if (confirm(`Retirer la surcharge de ${product.ref} ? Le produit reviendra à ses valeurs ERP.`)) {
                remove.mutate(product.ref, { onSuccess: onClose });
              }
            }}
          >
            <RotateCcw size={14} /> Retirer la surcharge
          </button>
          <div className="flex gap-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>Annuler</button>
            <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={save.isPending}>
              {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
